<template>
  <div class="select-map">
    <div class="select-map__container" ref="containerRef">
      <el-input
        v-model="inputValue"
        :placeholder="placeholder"
        @focus="handleInputFocus"
        @blur="handleInputBlur"
        @input="handleInputChange"
        clearable
        @clear="handleClear"
      />
      <div class="select-map__dropdown" v-if="showDropdown && searchResults.length > 0">
        <div
          v-for="item in searchResults"
          :key="item.id"
          class="select-map__option"
          @click="handleOptionClick(item)"
        >
          <div class="option-content">
            <div class="option-title">{{ item.title }}</div>
            <div class="option-address">{{ item.address }}</div>
            <!-- <el-tooltip class="box-item" effect="dark" :content="item.address" placement="top">
              <div class="option-address">{{ item.address }}</div>
            </el-tooltip> -->
          </div>
        </div>
      </div>
      <div class="select-map__loading" v-if="loading">搜索中...</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { publicData } from '@/utils/publicConfig'
import { baiduPlaceSuggestion } from '@/utils/baiduMapWebService'
import { $message } from '@/utils/message'
/**
 * 组件使用方法
 * @param modelValue 组件绑定的值，用于显示和更新地址
 * @param placeholder 输入框占位符
 * @param searchType 搜索类型，默认关键词搜索
 * @param searchCity 搜索城市，默认成都
 * const formData = [
 * {
 *   label: '地址',
 *   field: 'address',
 *   component: 'SelectMap',
 *   componentProps: {
 *      modelValue: 'modelValue',
 *      placeholder: '请输入地址搜索',
 *      searchType: 'keyword',
 *      searchCity: '成都',
 *      onSelect: (val) => {
 *        console.log('选中的整条数据', val)
 *      }
 *   }
 * ]
 * */
// 定义组件属性
interface SelectMapProps {
  modelValue?: any
  placeholder?: string
  searchType?: string
  searchCity?: string | number
}

// 默认属性值
const props = withDefaults(defineProps<SelectMapProps>(), {
  modelValue: null,
  placeholder: '请输入地址搜索',
  searchType: 'keyword', // 默认关键词搜索
  searchCity: publicData.cityName // 默认搜索城市
})

// 定义事件
const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'select', value: any): void
}>()

interface SelectMapOption {
  id: string
  title: string
  address: string
  province?: string
  city?: string
  district?: string
  location?: { lat: number; lng: number }
}

// 响应式数据
const internalValue = ref(props.modelValue)
const inputValue = ref('')
const searchResults = ref<SelectMapOption[]>([])
const loading = ref(false)
const showDropdown = ref(false)
const searchTimer = ref<number>()
const containerRef = ref<HTMLElement>()

// 初始化
onMounted(() => {
  // 初始化输入框值
  if (internalValue.value) {
    inputValue.value = getDisplayValue(internalValue.value)
  }
  // 添加点击外部关闭下拉框的事件
  document.addEventListener('click', handleDocumentClick)
})

// 监听内部值变化
watch(internalValue, (newValue) => {
  if (newValue) {
    emit('update:modelValue', getDisplayValue(newValue))
    // emit('select', newValue)
    inputValue.value = getDisplayValue(newValue)
  } else {
    emit('update:modelValue', null)
    emit('select', null)
  }
})

// 监听外部值变化
watch(
  () => props.modelValue,
  (newValue) => {
    internalValue.value = newValue
    if (newValue) {
      inputValue.value = getDisplayValue(newValue)
    }
  }
)

// 获取显示值
const getDisplayValue = (value: any): string => {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (value.title) return value.title
  return ''
}

// 处理输入框聚焦
const handleInputFocus = () => {
  showDropdown.value = true
  // 确保光标在文本末尾
  setTimeout(() => {
    const input = containerRef.value?.querySelector('input') as HTMLInputElement
    if (input) {
      const length = input.value.length
      input.setSelectionRange(length, length)
    }
  }, 0)
}

// 处理输入框失焦
const handleInputBlur = () => {
  // 延迟关闭，确保点击选项时能触发点击事件
  setTimeout(() => {
    showDropdown.value = false
  }, 400)
}

// 处理输入框输入变化
const handleInputChange = (value: string) => {
  debouncedSearch(value)
}

// 处理清空
const handleClear = () => {
  reset()
}

// 重置方法
const reset = () => {
  inputValue.value = ''
  internalValue.value = null
  searchResults.value = []
  showDropdown.value = false
  emit('update:modelValue', null)
  emit('select', null)
}

// 处理选项点击
const handleOptionClick = (item: any) => {
  emit('select', item)
  internalValue.value = item
  inputValue.value = item.title
  showDropdown.value = false
}

// 处理文档点击，关闭下拉框
const handleDocumentClick = (event: MouseEvent) => {
  if (containerRef.value && !containerRef.value.contains(event.target as Node)) {
    showDropdown.value = false
  }
}

// 搜索地址（百度地点输入提示）
const searchAddress = async (keyword: string) => {
  if (!keyword || keyword.trim().length < 2) {
    searchResults.value = []
    return
  }

  loading.value = true
  try {
    const data = await baiduPlaceSuggestion(keyword.trim(), String(props.searchCity || publicData.cityName))
    if (data.status === 0 && Array.isArray(data.result)) {
      searchResults.value = data.result.map((item, index) => ({
        id: item.uid || `baidu-suggest-${index}`,
        title: item.name || '',
        address: [item.city, item.district, item.address].filter(Boolean).join(' '),
        province: item.province,
        city: item.city,
        district: item.district,
        location: item.location
      }))
      showDropdown.value = true
    } else {
      searchResults.value = []
      if (data.message) {
        $message.error(data.message)
      }
    }
  } catch {
    searchResults.value = []
  } finally {
    loading.value = false
  }
}

// 防抖搜索
const debouncedSearch = (keyword: string) => {
  clearTimeout(searchTimer.value)
  searchTimer.value = window.setTimeout(() => {
    searchAddress(keyword)
  }, 300)
}

// 暴露方法给父组件
defineExpose({
  searchAddress,
  debouncedSearch,
  reset
})
</script>

<style scoped lang="less">
.select-map {
  position: relative;

  &__container {
    position: relative;
    width: 100%;
  }

  &__dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    left: 0;
    z-index: 1000;
    max-height: 300px;
    margin-top: 4px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgb(0 0 0 / 10%);
  }

  &__option {
    padding: 5px 12px;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: #f5f7fa;
    }
  }

  &__loading {
    position: absolute;
    top: 100%;
    right: 0;
    left: 0;
    margin-top: 4px;
    font-size: 12px;
    color: #606266;
    text-align: center;
    background: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
  }

  &__search-icon {
    color: #909399;
  }

  .option-content {
    .option-title {
      font-size: 14px;
      color: #303133;
    }

    .option-address {
      overflow: hidden;
      font-size: 12px;
      color: #909399;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
